import React, { useMemo, useState } from "react";
import { Row, Col, Card, CardBody, CardHeader, Table, Badge } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import "moment/locale/es";
import { formatCurrency } from "../../utils/formatCurrency";
import PeriodSelector from "./PeriodSelector";
import ChartTypeToggle from "./ChartTypeToggle";
import PaymentBreakdown from "./PaymentBreakdown";
import RevenueBreakdown from "./RevenueBreakdown";
import ExpandableChart from "./ExpandableChart";
import { CategoryIncome } from "./CategoryBreakdown";
import { SourceSelector, applySourceFilter } from "./sourceFilter";
import { sinDiaEnCurso } from "./partialDay";
import { claseVariacion, flechaVariacion } from "./polaridad";
import { TOOLBAR_DESCARGA, LEYENDA, SIN_LEYENDA } from "./graficos";
import { BotonesExportar } from "./exportar";
import { TITULO_CARD, TITULO_ACLARACION } from "./tituloCard";
// Una sola paleta para toda la pantalla: el color se pide por concepto.
import { MODALIDAD, ORIGEN, POLARIDAD, TIEMPO, UI } from "../Stats2/theme";

const fmt = (v) => formatCurrency(v ?? 0, true);

// ── Summary cards with period comparison ─────────────────────
const SUMMARY_ITEMS = [
  { key: "income", label: "Ingresos", icon: "attach_money", color: "success" },
  { key: "expense", label: "Salidas de caja", icon: "money_off", color: "danger", hideOn: ["monthly"] },
  { key: "net", label: "Neto", icon: "account_balance", color: null, hideOn: ["monthly"] },
  { key: "dailyAvg", label: "Promedio por día", icon: "calendar_today", color: "primary" },
  { key: "avgTicket", label: "Ticket promedio", icon: "receipt", color: "info", hideOn: ["monthly"] },
  { key: "days", label: "Días", icon: "date_range", color: "secondary", isNumber: true },
];

const calcStats = (docs) => {
  const income = docs.reduce((a, d) => a + (d.income || 0), 0);
  const expense = docs.reduce((a, d) => a + (d.expense || 0), 0);
  const ticketsOut = docs.reduce((a, d) => a + (d.tickets?.total?.out || 0), 0);
  const ticketAmt = docs.reduce((a, d) => a + (d.tickets?.total?.amount || 0), 0);
  const avgTicket = ticketsOut > 0 ? ticketAmt / ticketsOut : 0;

  // El promedio diario se calcula sobre días COMPLETOS: dividir el total
  // —que sí incluye el día en curso— por un día a medio facturar da un número
  // distinto del que muestra la pestaña Resumen para el mismo período.
  const completos = sinDiaEnCurso(docs);
  const incomeCompletos = completos.reduce((a, d) => a + (d.income || 0), 0);
  const dailyAvg = completos.length > 0
    ? incomeCompletos / completos.length
    : (docs.length > 0 ? income / docs.length : 0);

  return {
    income, expense, net: income - expense, avgTicket, dailyAvg,
    days: docs.length,
    diasCompletos: completos.length,
  };
};

const PeriodSummary = ({ docs, prevDocs, source = "all" }) => {
  const stats = useMemo(() => calcStats(docs), [docs]);
  const prevStats = useMemo(() => calcStats(prevDocs), [prevDocs]);

  const getChange = (key) => {
    const curr = stats[key];
    const prev = prevStats[key];
    if (!prev || prev === 0) return null;
    return ((curr - prev) / Math.abs(prev)) * 100;
  };

  const visibleItems = SUMMARY_ITEMS.filter((item) => !item.hideOn || !item.hideOn.includes(source));

  return (
    <Row className="g-3 mb-3">
      {visibleItems.map((item) => {
        const value = stats[item.key];
        const change = getChange(item.key);
        const colorClass = item.color === null
          ? (stats.net >= 0 ? "text-success" : "text-danger")
          : `text-${item.color}`;
        const borderColor = item.color === null
          ? (stats.net >= 0 ? "var(--bs-success)" : "var(--bs-danger)")
          : `var(--bs-${item.color})`;
        return (
          <Col xl={2} lg={4} md={4} sm={6} xs={6} key={item.key}>
            <Card className="shadow-sm h-100 mb-0" style={{ borderLeft: `3px solid ${borderColor}` }}>
              <CardBody className="py-3 px-3">
                <div className="text-muted mb-2" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>{item.icon}</span>
                  {item.label}
                </div>
                <h4 className={`${colorClass} mb-0`}>
                  {item.isNumber ? value : fmt(value)}
                </h4>
                {/* El promedio se calcula sobre días completos: si el período
                    incluye el día en curso, el divisor no es la cantidad de
                    días del período y hay que decirlo, o el número no cierra
                    para quien lo divida a mano. */}
                {item.key === "dailyAvg" && stats.diasCompletos !== stats.days && (
                  <div className="text-muted" style={{ fontSize: 10, marginTop: 2 }}>
                    sobre {stats.diasCompletos} días completos
                  </div>
                )}
                {item.key === "days" && stats.diasCompletos !== stats.days && (
                  <div className="text-muted" style={{ fontSize: 10, marginTop: 2 }}>
                    {stats.diasCompletos} días cerrados y el de hoy, que sigue abierto
                  </div>
                )}
                {change !== null && !item.isNumber && (
                  <div style={{ fontSize: 11, marginTop: 4 }}>
                    {/* El color sale de QUE subio, no del signo: que suban
                        las salidas de caja no es una buena noticia. */}
                    <span className={claseVariacion(change, item.key)}>
                      <span className="material-symbols-rounded" style={{ fontSize: 12, verticalAlign: "middle" }}>
                        {flechaVariacion(change)}
                      </span>
                      {Math.abs(change).toFixed(1)}%
                    </span>
                    <span className="text-muted ms-1">vs el período anterior</span>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};

// ── Main income chart with moving average + previous period ──
const IncomeChart = ({ docs, prevDocs, showMA, heightOverride }) => {
  const [chartType, setChartType] = useState("area");
  const sorted = useMemo(() => [...docs].sort((a, b) => a.date.localeCompare(b.date)), [docs]);
  const categories = useMemo(() => sorted.map((d) => moment(d.date).format("DD/MM")), [sorted]);

  const series = useMemo(() => {
    const s = [
      { name: "Ingresos", data: sorted.map((d) => d.income || 0) },
      { name: "Salidas de caja", data: sorted.map((d) => d.expense || 0) },
    ];

    // 7-day moving average
    if (showMA && sorted.length >= 7) {
      const incomes = sorted.map((d) => d.income || 0);
      const ma = incomes.map((_, i) => {
        if (i < 6) return null;
        const window = incomes.slice(i - 6, i + 1);
        return Math.round(window.reduce((a, b) => a + b, 0) / 7);
      });
      s.push({ name: "Tendencia de 7 días", data: ma, type: "line" });
    }

    // Previous period overlay
    if (prevDocs.length > 0) {
      const prevSorted = [...prevDocs].sort((a, b) => a.date.localeCompare(b.date));
      const prevIncomes = prevSorted.map((d) => d.income || 0);
      // Pad or trim to match current length
      const aligned = sorted.map((_, i) => prevIncomes[i] ?? null);
      s.push({ name: "Período anterior", data: aligned, type: "line" });
    }

    return s;
  }, [sorted, prevDocs, showMA]);

  // El orden de esta lista sigue al de `series`, una a una: lo que entra y lo
  // que sale llevan POLARIDAD, la tendencia no es una serie sino una linea de
  // referencia sobre los ingresos —por eso va en el neutro oscuro y no se lleva
  // un color de identidad— y el periodo anterior siempre en gris, detras.
  const colors = useMemo(() => {
    const c = [POLARIDAD.entra.color, POLARIDAD.sale.color];
    if (showMA && sorted.length >= 7) c.push(UI.texto);
    if (prevDocs.length > 0) c.push(TIEMPO.anterior.color);
    return c;
  }, [showMA, sorted.length, prevDocs.length]);

  const options = useMemo(
    () => ({
      chart: {
        type: chartType, height: heightOverride || 340, ...TOOLBAR_DESCARGA,
        zoom: { enabled: false }, fontFamily: "inherit",
        animations: { dynamicAnimation: { enabled: false } },
      },
      colors,
      dataLabels: { enabled: false },
      stroke: {
        curve: "smooth",
        width: series.map((s, i) => {
          if (s.name === "Tendencia de 7 días") return 3;
          if (s.name === "Período anterior") return 2;
          return chartType === "bar" ? 0 : 2.5;
        }),
        dashArray: series.map((s) => s.name === "Período anterior" ? 5 : 0),
      },
      fill: chartType === "area"
        ? { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05 } }
        : chartType === "bar"
          ? { opacity: 1 }
          : { opacity: 1 },
      plotOptions: chartType === "bar" ? { bar: { columnWidth: "55%", borderRadius: 4 } } : {},
      xaxis: { categories, tickAmount: Math.min(sorted.length, 15), labels: { style: { fontSize: "10px" }, rotate: -45, rotateAlways: sorted.length > 15, hideOverlappingLabels: true } },
      yaxis: { labels: { formatter: (v) => formatCurrency(v), style: { fontSize: "11px" } } },
      tooltip: { y: { formatter: (v) => v != null ? formatCurrency(v, true) : "-" } },
      // Hasta cuatro series encimadas —lo que entra, lo que sale, la tendencia
      // y el período anterior—: la leyenda es lo único que las separa.
      legend: LEYENDA,
      grid: { borderColor: "#f1f1f1" },
    }),
    [chartType, categories, sorted.length, colors, series]
  );

  return (
    <Card className="shadow-sm mb-3">
      <CardHeader className="bg-white py-2 d-flex align-items-center justify-content-between">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>trending_up</span>
          Lo que entró y lo que salió, día por día
        </h6>
        <ChartTypeToggle value={chartType} onChange={setChartType} />
      </CardHeader>
      <CardBody className="pt-1">
        <ReactApexChart key={chartType} options={options} series={series} type={chartType} height={heightOverride || 340} />
      </CardBody>
    </Card>
  );
};

// ── Cumulative income chart ─────────────────────────────────
const CumulativeChart = ({ docs, prevDocs, heightOverride }) => {
  const sorted = useMemo(() => [...docs].sort((a, b) => a.date.localeCompare(b.date)), [docs]);

  const series = useMemo(() => {
    let cumIncome = 0;
    const incomeData = sorted.map((d) => {
      cumIncome += d.income || 0;
      return cumIncome;
    });

    // "Acumulado actual" y "Acumulado anterior" son la misma palabra dos veces:
    // lo que hay que distinguir en la leyenda es CUÁNDO, no qué.
    const s = [{ name: "Este período", data: incomeData }];

    if (prevDocs.length > 0) {
      const prevSorted = [...prevDocs].sort((a, b) => a.date.localeCompare(b.date));
      let cumPrev = 0;
      const prevData = sorted.map((_, i) => {
        if (i < prevSorted.length) {
          cumPrev += prevSorted[i].income || 0;
          return cumPrev;
        }
        return null;
      });
      s.push({ name: "Período anterior", data: prevData });
    }

    return s;
  }, [sorted, prevDocs]);

  const options = useMemo(
    () => ({
      chart: { type: "area", height: heightOverride || 250, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
      // Acumulado del periodo contra el acumulado anterior: ahora contra antes.
      colors: [ORIGEN.tickets.color, TIEMPO.anterior.color],
      stroke: { curve: "smooth", width: [2.5, 2], dashArray: [0, 5] },
      fill: {
        type: "gradient",
        gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05 },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: sorted.map((d) => moment(d.date).format("DD/MM")),
        tickAmount: Math.min(sorted.length, 15), labels: { style: { fontSize: "10px" }, rotate: -45, rotateAlways: sorted.length > 15, hideOverlappingLabels: true },
      },
      yaxis: { labels: { formatter: (v) => formatCurrency(v), style: { fontSize: "11px" } } },
      tooltip: { y: { formatter: (v) => v != null ? formatCurrency(v, true) : "-" } },
      // Con período anterior son dos curvas del mismo tipo: la leyenda dice
      // cuál es cuál. Sin período anterior queda una sola serie y ApexCharts
      // no dibuja nada, que es lo correcto.
      legend: LEYENDA,
      grid: { borderColor: "#f1f1f1" },
    }),
    [sorted]
  );

  return (
    <Card className="shadow-sm">
      <CardHeader className="bg-white py-2">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>show_chart</span>
          Cómo se va sumando la plata del período
          {prevDocs.length > 0 && (
            <span className="ms-1" style={TITULO_ACLARACION}>
              (la línea punteada es el período anterior)
            </span>
          )}
        </h6>
      </CardHeader>
      <CardBody className="pt-1">
        <ReactApexChart options={options} series={series} type="area" height={heightOverride || 250} />
      </CardBody>
    </Card>
  );
};

// ── Avg ticket trend line ────────────────────────────────────
const AvgTicketChart = ({ docs, heightOverride }) => {
  const [chartType, setChartType] = useState("line");
  const sorted = useMemo(() => [...docs].sort((a, b) => a.date.localeCompare(b.date)), [docs]);

  const data = useMemo(() => {
    return sorted.map((d) => {
      const out = d.tickets?.total?.out || 0;
      const amt = d.tickets?.total?.amount || 0;
      return out > 0 ? Math.round(amt / out) : 0;
    });
  }, [sorted]);

  const options = useMemo(
    () => ({
      chart: { type: chartType, height: heightOverride || 200, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
      // Una sola serie, y es plata de tickets: el azul de Tickets.
      colors: [ORIGEN.tickets.color],
      stroke: { curve: "smooth", width: chartType === "bar" ? 0 : 2.5 },
      fill: chartType === "area"
        ? { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05 } }
        : chartType === "bar"
          ? { opacity: 1 }
          : { opacity: 1 },
      plotOptions: chartType === "bar" ? { bar: { columnWidth: "55%", borderRadius: 4 } } : {},
      xaxis: {
        categories: sorted.map((d) => moment(d.date).format("DD/MM")),
        tickAmount: Math.min(sorted.length, 15), labels: { style: { fontSize: "10px" }, rotate: -45, rotateAlways: sorted.length > 15, hideOverlappingLabels: true },
      },
      yaxis: { labels: { formatter: (v) => formatCurrency(v), style: { fontSize: "11px" } } },
      tooltip: { y: { formatter: (v) => formatCurrency(v, true) } },
      // Una sola serie y un solo color: el título "Cuánto te deja cada auto
      // que sale" ya la nombra, y un cuadradito repitiéndolo sería ruido.
      legend: SIN_LEYENDA,
      grid: { borderColor: "#f1f1f1" },
      dataLabels: { enabled: false },
    }),
    [sorted, chartType]
  );

  return (
    <Card className="shadow-sm">
      <CardHeader className="bg-white py-2 d-flex align-items-center justify-content-between">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>receipt</span>
          Cuánto te deja cada auto que sale
        </h6>
        <ChartTypeToggle value={chartType} onChange={setChartType} />
      </CardHeader>
      <CardBody className="pt-1">
        <ReactApexChart key={chartType} options={options} series={[{ name: "Ticket promedio", data }]} type={chartType} height={heightOverride || 200} />
      </CardBody>
    </Card>
  );
};

// ── Stacked revenue sources over time ────────────────────────
const RevenueStackedChart = ({ docs, heightOverride }) => {
  const [chartType, setChartType] = useState("bar");
  const sorted = useMemo(() => [...docs].sort((a, b) => a.date.localeCompare(b.date)), [docs]);

  // Cada serie viaja con su color. Las cuatro primeras son MODALIDADES DE
  // COBRO, no orígenes distintos: por eso llevan "Tickets" adelante —"Estadía"
  // a secas parecía contestar cuánto se queda la gente— y por eso llevan la
  // rampa del azul de Tickets, ordenada por duración, en vez de cuatro tonos
  // sueltos. Abonados y Acuerdos sí son orígenes propios.
  const series = useMemo(
    () => [
      { name: "Tickets cobrados por hora", color: MODALIDAD.hour.color, data: sorted.map((d) => d.tickets?.hour?.amount || 0) },
      { name: "Tickets cobrados por estadía", color: MODALIDAD.stay.color, data: sorted.map((d) => d.tickets?.stay?.amount || 0) },
      { name: "Tickets cobrados por turno", color: MODALIDAD.tn.color, data: sorted.map((d) => d.tickets?.tn?.amount || 0) },
      { name: "Tickets con tarifa plana", color: MODALIDAD.plana.color, data: sorted.map((d) => d.tickets?.plana?.amount || 0) },
      { name: "Abonados", color: ORIGEN.abonados.color, data: sorted.map((d) => d.monthly?.amount || 0) },
      { name: "Acuerdos", color: ORIGEN.convenios.color, data: sorted.map((d) => d.deals?.amount || 0) },
    ].filter((s) => s.data.some((v) => v > 0)),
    [sorted]
  );

  const options = useMemo(
    () => ({
      chart: { type: chartType, stacked: true, height: heightOverride || 280, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
      // El color sale de la serie que quedó, no de una lista paralela: las
      // series vacías se filtran, y con una lista fija el que se corría un
      // puesto se llevaba el color del de arriba.
      colors: series.map((s) => s.color),
      stroke: { curve: "smooth", width: chartType === "bar" ? 0 : 2 },
      fill: chartType === "area"
        ? { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1 } }
        : chartType === "bar"
          ? { opacity: 1 }
          : { opacity: 1 },
      plotOptions: chartType === "bar" ? { bar: { columnWidth: "60%", borderRadius: 2 } } : {},
      dataLabels: { enabled: false },
      xaxis: {
        categories: sorted.map((d) => moment(d.date).format("DD/MM")),
        tickAmount: Math.min(sorted.length, 15), labels: { style: { fontSize: "10px" }, rotate: -45, rotateAlways: sorted.length > 15, hideOverlappingLabels: true },
      },
      yaxis: { labels: { formatter: (v) => formatCurrency(v), style: { fontSize: "11px" } } },
      tooltip: { y: { formatter: (v) => formatCurrency(v, true) } },
      // Seis franjas apiladas del mismo alto: sin leyenda, el gráfico dice que
      // entró plata pero no de dónde, que es justo lo que vino a contestar.
      legend: LEYENDA,
      grid: { borderColor: "#f1f1f1" },
    }),
    [sorted, chartType, series]
  );

  return (
    <Card className="shadow-sm">
      <CardHeader className="bg-white py-2 d-flex align-items-center justify-content-between">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>stacked_bar_chart</span>
          De dónde salió la plata cada día
        </h6>
        <ChartTypeToggle value={chartType} onChange={setChartType} />
      </CardHeader>
      <CardBody className="pt-1">
        <ReactApexChart key={chartType} options={options} series={series} type={chartType} height={heightOverride || 280} />
      </CardBody>
    </Card>
  );
};

// ── Daily detail table ──────────────────────────────────────
const DailyDetailTable = ({ docs, source = "all" }) => {
  const [sortKey, setSortKey] = useState("date");
  const [sortAsc, setSortAsc] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  const rows = useMemo(() => {
    return docs.map((d) => {
      const ticketsOut = d.tickets?.total?.out || 0;
      const ticketAmt = d.tickets?.total?.amount || 0;
      return {
        date: d.date,
        dateLabel: moment(d.date).format("ddd DD/MM"),
        income: d.income || 0,
        expense: d.expense || 0,
        net: (d.income || 0) - (d.expense || 0),
        tickets: ticketsOut,
        avgTicket: ticketsOut > 0 ? Math.round(ticketAmt / ticketsOut) : 0,
      };
    });
  }, [docs]);

  const sorted = useMemo(() => {
    const s = [...rows].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === "string") return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortAsc ? va - vb : vb - va;
    });
    return s;
  }, [rows, sortKey, sortAsc]);

  // El dia en curso queda afuera: a media manana SIEMPRE seria el "peor dia",
  // y esa medalla no describe al dia, describe que todavia no termino.
  const cerrados = useMemo(() => sinDiaEnCurso(rows), [rows]);
  const bestDay = useMemo(() => cerrados.reduce((best, r) => r.income > best.income ? r : best, cerrados[0]), [cerrados]);
  const worstDay = useMemo(() => cerrados.reduce((worst, r) => r.income < worst.income ? r : worst, cerrados[0]), [cerrados]);

  const displayed = collapsed ? sorted.slice(0, 10) : sorted;

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(key === "date"); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span className="material-symbols-rounded text-muted opacity-25 ms-1" style={{ fontSize: 12 }}>unfold_more</span>;
    return (
      <span className="material-symbols-rounded ms-1" style={{ fontSize: 12 }}>
        {sortAsc ? "arrow_upward" : "arrow_downward"}
      </span>
    );
  };

  const allColumns = [
    { key: "date", label: "Fecha", align: "start" },
    { key: "income", label: "Ingresos", align: "end" },
    { key: "expense", label: "Salidas de caja", align: "end", hideOn: ["monthly"] },
    { key: "net", label: "Neto", align: "end", hideOn: ["monthly"] },
    { key: "tickets", label: "Tickets", align: "end", hideOn: ["monthly"] },
    { key: "avgTicket", label: "Ticket promedio", align: "end", hideOn: ["monthly"] },
  ];
  const columns = allColumns.filter((c) => !c.hideOn || !c.hideOn.includes(source));

  if (!rows.length) return null;

  return (
    <Card className="shadow-sm mt-3">
      <CardHeader className="bg-white py-2 d-flex align-items-center justify-content-between">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>table_chart</span>
          Día por día, el detalle completo
          <span className="ms-1" style={TITULO_ACLARACION}>
            ({rows.length} días{rows.length > 3 ? " · el mejor y el peor van marcados" : ""})
          </span>
        </h6>
        <BotonesExportar
          columnas={columns.map((c) => (c.key === "date"
            ? { ...c, valor: (f) => moment(f.date).format("DD/MM/YYYY") }
            : c))}
          filas={sorted}
          nombre="detalle-diario-ingresos"
        />
      </CardHeader>
      <CardBody className="py-0 px-0">
        <div style={{ maxHeight: collapsed ? undefined : 500, overflowY: collapsed ? undefined : "auto" }}>
          <Table responsive hover size="sm" className="mb-0" style={{ fontSize: 13 }}>
            <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`text-${col.align}`}
                    style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}<SortIcon col={col.key} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map((r) => {
                const isBest = bestDay && r.date === bestDay.date && rows.length > 3;
                const isWorst = worstDay && r.date === worstDay.date && rows.length > 3;
                return (
                  <tr key={r.date} style={isBest ? { backgroundColor: "rgba(52,195,143,0.08)" } : isWorst ? { backgroundColor: "rgba(244,106,106,0.08)" } : undefined}>
                    {columns.map((col) => {
                      if (col.key === "date") return (
                        <td key={col.key}>
                          {r.dateLabel}
                          {isBest && <Badge color="success" pill className="ms-2" style={{ fontSize: 10 }}>Mejor</Badge>}
                          {isWorst && <Badge color="danger" pill className="ms-2" style={{ fontSize: 10 }}>Peor</Badge>}
                        </td>
                      );
                      if (col.key === "income") return <td key={col.key} className="text-end text-success">{fmt(r.income)}</td>;
                      if (col.key === "expense") return <td key={col.key} className="text-end text-danger">{fmt(r.expense)}</td>;
                      if (col.key === "net") return <td key={col.key} className={`text-end ${r.net >= 0 ? "text-success" : "text-danger"}`}>{fmt(r.net)}</td>;
                      if (col.key === "tickets") return <td key={col.key} className="text-end">{r.tickets}</td>;
                      if (col.key === "avgTicket") return <td key={col.key} className="text-end">{fmt(r.avgTicket)}</td>;
                      return null;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        {rows.length > 10 && (
          <div className="text-center py-2 border-top">
            <button
              className="btn btn-sm btn-link text-muted text-decoration-none"
              onClick={() => setCollapsed(!collapsed)}
            >
              <span className="material-symbols-rounded me-1" style={{ fontSize: 14, verticalAlign: "middle" }}>
                {collapsed ? "expand_more" : "expand_less"}
              </span>
              {collapsed ? `Ver todos (${rows.length})` : "Ver menos"}
            </button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

// ── Tab principal ────────────────────────────────────────────
const TabIngresos = ({ dailyDocs, liveData, periodo = null }) => {
    // `periodo` es OPCIONAL y viene de afuera. Si llega, manda el de afuera y
  // este componente oculta su propio selector: dos selectores mostrando
  // periodos distintos en la misma vista es justo la incoherencia que
  // estamos sacando de esta pantalla. Si no llega —que es como lo usa la
  // pantalla de siempre— se comporta exactamente igual que antes.
  const [periodLocal, setPeriod] = useState(30);
  const period = periodo ?? periodLocal;
  const [showMA, setShowMA] = useState(true);
  const [showPrev, setShowPrev] = useState(true);
  const [source, setSource] = useState("all");

  const filtered = useMemo(
    () => applySourceFilter(dailyDocs.slice(0, period), source),
    [dailyDocs, period, source]
  );

  // Previous equivalent period for comparison
  const prevFiltered = useMemo(
    () => showPrev ? applySourceFilter(dailyDocs.slice(period, period * 2), source) : [],
    [dailyDocs, period, showPrev, source]
  );

  // Original unfiltered docs for composition charts (only used when source === "all")
  const unfilteredDocs = useMemo(
    () => dailyDocs.slice(0, period),
    [dailyDocs, period]
  );

  return (
    <>
      {/* Controls + summary */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <div className="d-flex align-items-center flex-wrap gap-2">
          {periodo == null && (
            <PeriodSelector value={period} onChange={setPeriod} maxDays={dailyDocs.length} />
          )}
          <SourceSelector value={source} onChange={setSource} />
        </div>
        <div className="d-flex gap-2">
          <button
            className={`btn btn-sm ${showMA ? "btn-soft-warning" : "btn-outline-secondary"}`}
            onClick={() => setShowMA(!showMA)}
          >
            <span className="material-symbols-rounded" style={{ fontSize: 14, verticalAlign: "middle" }}>
              {showMA ? "check_box" : "check_box_outline_blank"}
            </span>
            <span className="ms-1" style={{ fontSize: 12 }}>Tendencia</span>
          </button>
          {dailyDocs.length > period && (
            <button
              className={`btn btn-sm ${showPrev ? "btn-soft-info" : "btn-outline-secondary"}`}
              onClick={() => setShowPrev(!showPrev)}
            >
              <span className="material-symbols-rounded" style={{ fontSize: 14, verticalAlign: "middle" }}>
                {showPrev ? "check_box" : "check_box_outline_blank"}
              </span>
              <span className="ms-1" style={{ fontSize: 12 }}>Comparar</span>
            </button>
          )}
        </div>
      </div>
      {/* Explanation of active toggles */}
      {(showMA || showPrev) && (
        <div className="mb-3" style={{ fontSize: 12, lineHeight: 1.6 }}>
          {showMA && (
            <div className="d-flex align-items-start text-muted">
              {/* Estos dos rotulos tienen que salir del MISMO color que la
                  linea que describen: si no, la ayuda manda a buscar en el
                  grafico una linea de un color que ahi no existe. */}
              <span className="material-symbols-rounded me-1 flex-shrink-0" style={{ fontSize: 13, color: UI.texto, marginTop: 2 }}>show_chart</span>
              <span>
                <strong style={{ color: UI.texto }}>Tendencia:</strong> dibuja una línea con el promedio de los últimos
                7 días, para que veas si los ingresos van subiendo o bajando sin que un día muy bueno o muy malo te confunda.
              </span>
            </div>
          )}
          {showPrev && dailyDocs.length > period && (
            <div className="d-flex align-items-start text-muted mt-1">
              <span className="material-symbols-rounded me-1 flex-shrink-0" style={{ fontSize: 13, color: TIEMPO.anterior.color, marginTop: 2 }}>compare_arrows</span>
              <span>
                <strong style={{ color: UI.textoSuave }}>Comparar:</strong> superpone el período anterior
                (los {period} días previos) como línea punteada gris, para que veas si mejoraste o empeoraste respecto de él.
              </span>
            </div>
          )}
        </div>
      )}
      <PeriodSummary docs={filtered} prevDocs={prevFiltered} source={source} />

      {/* Main income chart */}
      <ExpandableChart title="Lo que entró y lo que salió, día por día">
        <IncomeChart docs={filtered} prevDocs={showPrev ? prevFiltered : []} showMA={showMA} />
      </ExpandableChart>

      {/* Cumulative + Avg ticket */}
      <Row className="g-3 mb-3">
        <Col xl={source === "monthly" ? 12 : 6}>
          <ExpandableChart title="Cómo se va sumando la plata del período">
            <CumulativeChart docs={filtered} prevDocs={showPrev ? prevFiltered : []} />
          </ExpandableChart>
        </Col>
        {source !== "monthly" && (
          <Col xl={6}>
            <ExpandableChart title="Cuánto te deja cada auto que sale">
              <AvgTicketChart docs={filtered} />
            </ExpandableChart>
          </Col>
        )}
      </Row>

      {/* Revenue composition over time — only when showing all sources */}
      {source === "all" && (
        <div className="mb-3">
          <ExpandableChart title="De dónde salió la plata cada día">
            <RevenueStackedChart docs={unfilteredDocs} />
          </ExpandableChart>
        </div>
      )}

      {/* Donuts: origin + payment methods — only when showing all sources */}
      {source === "all" && (
        <Row className="g-3 mb-3">
          <Col xl={6}>
            <RevenueBreakdown dailyDocs={unfilteredDocs} period={period} />
          </Col>
          <Col xl={6}>
            {/* Estaba en null a mano: el componente ya sabe preferir el dato de
                hoy y caer al agregado del período si no hay. El prop ya llegaba. */}
            <PaymentBreakdown livePayments={liveData?.payments} dailyDocs={unfilteredDocs} period={period} />
          </Col>
        </Row>
      )}

      {/* Income by category — only when showing all sources */}
      {source === "all" && (
        <div className="mb-3">
          <CategoryIncome docs={unfilteredDocs} />
        </div>
      )}

      {/* Daily detail table */}
      <DailyDetailTable docs={filtered} source={source} />
    </>
  );
};

export default TabIngresos;
